import { Link, useLocation } from "react-router-dom";
import { ChevronDownIcon, ChevronUpIcon } from "../icons/NavbarIcons";
import { scrollToSection } from "../../utils/scroll";
import { LANDING_SECTIONS } from "./constants";
import { ARTICLES_VISIBLE } from "../../config/features";
import React from "react";

type DesktopMenuProps = {
  isDropdownOpen: boolean;
  toggleDropdown: () => void;
  dropdownRef: React.RefObject<HTMLLIElement>;
  handleNavigation: (sectionId: string) => void;
};

export function DesktopMenu({
  isDropdownOpen,
  toggleDropdown,
  dropdownRef,
  handleNavigation,
}: DesktopMenuProps) {
  const location = useLocation();

  const isServicesPage = location.pathname.endsWith("/services");
  const isResourcesPage = location.pathname.endsWith("/resources");
  const isHomePage = location.pathname === "/";
  const isCareersPage = location.pathname.endsWith("/careers");
  const isBookingPage = location.pathname.endsWith("/booking");
  const isArticlesPage = ARTICLES_VISIBLE && location.pathname.startsWith("/articles");
  const isCartoonsPage = location.pathname.startsWith("/cartoons");

  const showHomeLink =
    isServicesPage ||
    isResourcesPage ||
    isCareersPage ||
    isBookingPage ||
    isArticlesPage ||
    isCartoonsPage;

  const linkClass =
    "py-1 md:py-1.5 inline-block md:px-1 lg:px-1.5 xl:px-2 text-[10px] lg:text-[11px] xl:text-xs 2xl:text-sm font-semibold hover:scale-105 transition-all ease-in-out duration-300";

  return (
    <div className="hidden md:block w-full md:w-auto" id="menu">
      <nav className="w-full md:bg-transparent rounded shadow-lg px-4 py-3 mt-3 text-center md:px-2 md:py-0 md:mt-0 md:shadow-none">
        <ul className="md:flex items-center">
          {/* Home Link */}
          {showHomeLink && (
            <li className="md:ml-1 lg:ml-1.5 xl:ml-2 2xl:ml-3">
              <Link to="/" className={linkClass}>
                Home
              </Link>
            </li>
          )}

          {/* Home Page Drodown */}
          {isHomePage && (
            <li
              className="md:ml-1 lg:ml-1.5 xl:ml-2 2xl:ml-3 relative"
              ref={dropdownRef}
            >
              <button
                onClick={toggleDropdown}
                className={`py-1 md:py-1.5 inline-flex items-center md:px-1 lg:px-1.5 xl:px-2 text-[10px] lg:text-[11px] xl:text-xs 2xl:text-sm font-semibold hover:scale-105 transition-all ease-in-out duration-300`}
              >
                Menu
                {isDropdownOpen ? (
                  <ChevronUpIcon className="ml-0.5 h-3 w-3 lg:h-3.5 lg:w-3.5 xl:h-4 xl:w-4" />
                ) : (
                  <ChevronDownIcon className="ml-0.5 h-3 w-3 lg:h-3.5 lg:w-3.5 xl:h-4 xl:w-4" />
                )}
              </button>

              {/* Sections Dropdown */}
              {isDropdownOpen && (
                <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div
                    className="py-1"
                    role="menu"
                    aria-orientation="vertical"
                  >
                    {LANDING_SECTIONS.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => handleNavigation(section.id)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 w-full text-left"
                      >
                        {section.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </li>
          )}

          <li className="md:ml-1 lg:ml-1.5 xl:ml-2 2xl:ml-3">
            <Link to="/booking" className={linkClass}>
              Book Now
            </Link>
          </li>

          <li className="md:ml-1 lg:ml-1.5 xl:ml-2 2xl:ml-3">
            <Link to="/services" className={linkClass}>
              Pay Here
            </Link>
          </li>

          <li className="md:ml-1 lg:ml-1.5 xl:ml-2 2xl:ml-3">
            <Link
              to="/resources"
              className={`${linkClass} animate-blink-orange`}
            >
              Resources
            </Link>
          </li>

          {ARTICLES_VISIBLE ? (
            <li className="md:ml-1 lg:ml-1.5 xl:ml-2 2xl:ml-3">
              <Link
                to="/articles"
                className={`${linkClass} ${isArticlesPage ? "text-cerulean" : ""}`}
              >
                Articles
              </Link>
            </li>
          ) : null}

          <li className="md:ml-1 lg:ml-1.5 xl:ml-2 2xl:ml-3">
            <Link
              to="/cartoons"
              className={`${linkClass} ${isCartoonsPage ? "text-cerulean" : ""}`}
            >
              Comics
            </Link>
          </li>

          <li className="md:ml-1 lg:ml-1.5 xl:ml-2 2xl:ml-3">
            <Link to="/careers" className={linkClass}>
              Careers
            </Link>
          </li>

          <li className="md:ml-2 lg:ml-3 xl:ml-4 2xl:ml-5 mt-2 md:mt-0">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => scrollToSection("appointment")}
            >
              Contact Us
            </button>
          </li>

          <li className="ml-1.5 lg:ml-2 xl:ml-3 2xl:ml-5">
            <img
              src="/assets/home/psychology-today-logo-160.webp"
              width={160}
              height={52}
              className="w-14 h-auto lg:w-16 xl:w-20 2xl:w-36"
              alt="Psychology Today"
            />
          </li>
        </ul>
      </nav>
    </div>
  );
}
